import {
  MonitorIcon,
  ClockIcon,
  CircleHelpIcon,
  TrendingDownIcon,
  LayersIcon,
  StethoscopeIcon,
  ActivityIcon,
  ScanLineIcon,
  ClipboardCheckIcon,
  MapIcon,
  FolderOpenIcon,
} from 'lucide-react';

export const MOCKUP_URL = 'https://cdn.magicpatterns.com/uploads/k9BqKixHqJHekQ44wurvEo/MOCKUP_TORQUE-EN.png';
export const AUTHOR_URL = 'https://cdn.magicpatterns.com/uploads/hjaHoLGjApEPfycUfsK9ix/VALERIA_MONTES_-_BIOMECANICA.png';

export const stats = [
  { value: '6', label: 'Specialized clinical volumes' },
  { value: '5', label: 'Exclusive practical bonuses' },
  { value: '15 min', label: 'Structured planning workflow' },
  { value: '100%', label: 'Digital learning materials' },
];

export const beforeItems = [
  'You struggle to distinguish controlled root movement from unwanted crown tipping.',
  'You’re unsure how to evaluate torque requirements in challenging cases.',
  'You depend heavily on software predictions without fully understanding the biomechanical limitations.',
  'You find it difficult to select appropriate auxiliary mechanics.',
  'You sometimes discover torque-related discrepancies during treatment.',
  'You lack a consistent framework for reviewing root movement objectives.',
];

export const afterItems = [
  'Follow a structured process to evaluate torque requirements.',
  'Understand the difference between tipping, translation, and root movement.',
  'Recognize biomechanical limitations before finalizing a plan.',
  'Evaluate attachments, power ridges, anchorage, and auxiliary mechanics.',
  'Identify potential discrepancies between planned and observed movements.',
  'Organize your clinical decisions with a repeatable visual framework.',
];

export const painPoints = [
  {
    icon: MonitorIcon,
    title: 'Your Digital Simulation Looks Perfect — But You’re Not Completely Convinced',
    text: 'The software shows the desired tooth position, but you’re unsure whether the planned root movement is biomechanically achievable.',
  },
  {
    icon: ClockIcon,
    title: 'You Spend Too Much Time Reviewing Torque Mechanics',
    text: 'You know the movement you want, but deciding how to approach it requires repeated analysis.',
  },
  {
    icon: CircleHelpIcon,
    title: 'You’re Unsure When Attachments or Power Ridges May Help',
    text: 'You understand their purpose, but selecting appropriate mechanics for different clinical situations can be challenging.',
  },
  {
    icon: TrendingDownIcon,
    title: 'You Encounter Unwanted Tipping',
    text: 'The crown moves, but the root does not follow the intended trajectory.',
  },
  {
    icon: LayersIcon,
    title: 'You’re Concerned About Torque Expression in Clear Aligner Treatments',
    text: 'You recognize that some movements are less predictable, but you want a better way to evaluate those limitations.',
  },
  {
    icon: StethoscopeIcon,
    title: 'You Want Greater Confidence When Reviewing Complex Cases',
    text: 'You want to understand the biomechanical reasoning behind your decisions instead of relying entirely on software-generated movements.',
  },
];

export const frameworkSteps = [
  { name: 'Identify', text: 'Define the intended tooth movement and root-position objective.' },
  { name: 'Analyze', text: 'Evaluate the relevant force system and biomechanical limitations.' },
  { name: 'Select', text: 'Consider appropriate mechanics and available alternatives.' },
  { name: 'Verify', text: 'Review anchorage, biological limitations, and potential risks.' },
  { name: 'Document', text: 'Organize the proposed plan and identify monitoring requirements.' },
];

export const learningTools = [
  { icon: ActivityIcon, title: 'Visual Biomechanics Diagrams', text: 'Understand how different force systems may influence crown and root movement.' },
  { icon: ScanLineIcon, title: 'Root Movement Illustrations', text: 'Compare tipping, translation, and torque through visual examples.' },
  { icon: ClipboardCheckIcon, title: 'Clinical Decision Checklists', text: 'Review relevant factors before selecting a mechanical approach.' },
  { icon: MapIcon, title: 'Torque Planning Decision Maps', text: 'Organize the evaluation of common torque-related challenges.' },
  { icon: FolderOpenIcon, title: 'Illustrated Clinical Scenarios', text: 'Practice applying biomechanical reasoning to educational case examples.' },
];

export const valueStack = [
  { name: 'Volume I — The Root Movement Foundation', value: 47 },
  { name: 'Volume II — The Torque Diagnosis Roadmap', value: 47 },
  { name: 'Volume III — The Force System Blueprint', value: 47 },
  { name: 'Volume IV — The Attachment & Auxiliary Mechanics Atlas', value: 47 },
  { name: 'Volume V — The Advanced Torque Planning Roadmap', value: 47 },
  { name: 'Volume VI — The Torque Verification & Finishing Protocol', value: 47 },
  { name: 'Bonus #1 — Torque Planning Checklist', value: 37 },
  { name: 'Bonus #2 — Root Movement Visual Atlas', value: 29 },
  { name: 'Bonus #3 — Attachment & Power Ridge Decision Guide', value: 27 },
  { name: 'Bonus #4 — Torque Tracking Assessment Workbook', value: 24 },
  { name: 'Bonus #5 — Orthodontic Torque Case Review Workbook', value: 37 },
];

export const authorQuestions = [
  'What movement am I trying to achieve?',
  'What force system is involved?',
  'What biomechanical limitations should I consider?',
  'How can I evaluate the movement throughout treatment?',
];

export const finalImagine = [
  'The intended root movement.',
  'The biomechanics behind your plan.',
  'The limitations of different appliances.',
  'The mechanical options available.',
  'The factors you need to monitor during treatment.',
];
